import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaEye } from 'react-icons/fa';
import { Tilt } from 'react-tilt';
import './Portfolio.css';
import { API_BASE_URL } from '../config';

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const filters = [
    { id: 'all', name: 'All' },
    { id: 'web', name: 'Web Development' },
    { id: 'ai', name: 'AI & Data Science' },
    { id: 'mobile', name: 'Mobile Apps' }
  ];

  // Sample projects data (in real app, this would come from API)
  const sampleProjects = useMemo(() => [
    {
      id: 1,
      title: 'Portfolio Website',
      description: 'A modern, responsive portfolio website built with React, Node.js, and MongoDB. Features include smooth animations, project filtering, and contact form integration.',
      image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&h=350&fit=crop&auto=format',
      technologies: ['React', 'Node.js', 'MongoDB', 'Express.js'],
      githubUrl: 'https://github.com/lakshmananr/portfolio',
      liveUrl: 'https://lakshmananr-portfolio.com',
      category: 'web',
      featured: true
    },
    {
      id: 2,
      title: 'Student Management System',
      description: 'A comprehensive student management system for educational institutions with attendance tracking, grade management, and reporting features.',
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&h=350&fit=crop&auto=format',
      technologies: ['React', 'Node.js', 'MongoDB', 'Express.js'],
      githubUrl: 'https://github.com/lakshmananr/student-management',
      liveUrl: 'https://student-management-demo.com',
      category: 'web',
      featured: true
    },
    {
      id: 3,
      title: 'Data Analysis Dashboard',
      description: 'Interactive dashboard for analyzing and visualizing data using Python, Pandas, and various visualization libraries.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=350&fit=crop&auto=format',
      technologies: ['Python', 'Pandas', 'Matplotlib', 'Jupyter'],
      githubUrl: 'https://github.com/lakshmananr/data-analysis',
      liveUrl: 'https://data-analysis-demo.com',
      category: 'ai',
      featured: false
    },
    {
      id: 4,
      title: 'E-Learning Platform',
      description: 'Online learning platform with course management, video streaming, and progress tracking for educational content.',
      image: 'https://images.unsplash.com/photo-1610484826967-09c5720778c7?w=600&h=350&fit=crop&auto=format',
      technologies: ['React', 'Node.js', 'MongoDB', 'Video.js'],
      githubUrl: 'https://github.com/lakshmananr/e-learning',
      liveUrl: 'https://e-learning-demo.com',
      category: 'web',
      featured: false
    },
    {
      id: 5,
      title: 'Inventory Management System',
      description: 'A comprehensive inventory management system for businesses with stock tracking, order management, and reporting features.',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=350&fit=crop&auto=format',
      technologies: ['React', 'Node.js', 'MySQL', 'Express.js'],
      githubUrl: 'https://github.com/lakshmananr/inventory-system',
      liveUrl: 'https://inventory-demo.com',
      category: 'web',
      featured: false
    },
    {
      id: 6,
      title: 'Machine Learning Model',
      description: 'A machine learning model for predictive analytics using Python, Scikit-learn, and data preprocessing techniques.',
      image: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=600&h=350&fit=crop&auto=format',
      technologies: ['Python', 'Scikit-learn', 'Pandas', 'NumPy'],
      githubUrl: 'https://github.com/lakshmananr/ml-model',
      liveUrl: 'https://ml-model-demo.com',
      category: 'ai',
      featured: false
    }
  ], []);


  useEffect(() => {
    // Show sample projects immediately so the section is never empty
    setProjects(sampleProjects);
    setLoading(false);

    // Then try to fetch live data from the backend (if running)
    const fetchProjects = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000); // 3s timeout
        const response = await fetch(`${API_BASE_URL}/api/projects`, {
          signal: controller.signal
        });
        clearTimeout(timeout);

        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setProjects(data); // override with live data only if non-empty
          }
        }
      } catch {
        // Backend not available — keep showing sampleProjects (already set above)
      }
    };

    fetchProjects();
  }, [sampleProjects]);

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(project => project.category === filter);

  return (
    <section className="portfolio section" id="portfolio">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Portfolio</h2>
          <p className="section-subtitle">My Projects</p>
        </motion.div>

        <motion.div
          className="portfolio-filters"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {filters.map((filterItem) => (
            <button
              key={filterItem.id}
              className={`filter-btn ${filter === filterItem.id ? 'active' : ''}`}
              onClick={() => setFilter(filterItem.id)}
            >
              {filterItem.name}
            </button>
          ))}
        </motion.div>

        {loading ? (
          <motion.div
            className="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="loading-spinner"></div>
            <p>Loading projects...</p>
          </motion.div>
        ) : (
          <motion.div
            className="portfolio-grid"
            layout
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="project-card"
              >
                <Tilt
                  options={{
                    max: 15,
                    scale: 1.05,
                    speed: 1000,
                    glare: true,
                    "max-glare": 0.2,
                  }}
                >
                  <div className="project-image">
                    <img src={project.image} alt={project.title} />
                    <div className="project-overlay">
                      <div className="project-actions">
                        <a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-action"
                        >
                          <FaGithub />
                        </a>
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-action"
                        >
                          <FaExternalLinkAlt />
                        </a>
                        <button className="project-action">
                          <FaEye />
                        </button>
                      </div>
                    </div>
                    {project.featured && (
                      <div className="featured-badge">Featured</div>
                    )}
                  </div>

                  <div className="project-content">
                    <h3 className="project-title">{project.title}</h3>
                    <p className="project-description">{project.description}</p>
                    <div className="project-technologies">
                      {project.technologies.map((tech, techIndex) => (
                        <span key={techIndex} className="tech-tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </Tilt>
              </motion.div>
            ))}
          </motion.div>
        )}

        {!loading && filteredProjects.length === 0 && (
          <motion.div
            className="no-projects"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>No projects found in this category.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Portfolio; 